import styled from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getToken } from '@optimus-web/common';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { Button } from '../../Button';
import { Chip } from '../../Chip';
import { Stack } from '../../Stack';
import { useTableContext } from '../TableContext';
import { tableStateReducer } from '../tableStateReducer';
import {
    BoolFilterType,
    ColumnFilter,
    ColumnFilterEntries,
    ColumnFilterType,
    EnumFilterType,
    Filter,
    ReducerActionType,
    TableFilters,
} from '../types';

interface TableActiveColumnFiltersProps {
    onRowSelectionChange?: React.Dispatch<React.SetStateAction<{}>>;
}

const TagsContainer = styled(Stack)<{ hasTags: boolean }>`
    margin-bottom: ${({ hasTags, theme }) => (hasTags ? `${getToken(TokenName.SPACING_200)({ theme })}px` : '0')};
`;

const TableActiveColumnFilters = <T, F extends TableFilters<T>>({
    onRowSelectionChange,
}: TableActiveColumnFiltersProps) => {
    const { columns, state, onStateChange } = useTableContext<T, F>();
    const { t } = useLocalization();

    const handleMultiSelectFilterRemove = (
        columnName: keyof T,
        columnFilter: Filter<EnumFilterType, string[]>,
        selectedValue: string
    ) => {
        const nextState = tableStateReducer<T, F>(state, {
            type: ReducerActionType.RemoveMultiSelectFilter,
            payload: {
                columnName,
                columnFilter,
                selectedValue,
            },
        });

        onStateChange(nextState);
        onRowSelectionChange?.({});
    };

    const handleFilterRemove = (columnName: keyof T, columnFilter: ColumnFilter) => {
        const nextState = tableStateReducer<T, F>(state, {
            type: ReducerActionType.RemoveFilter,
            payload: {
                columnName,
                columnFilter,
            },
        });

        onStateChange(nextState);
        onRowSelectionChange?.({});
    };

    const handleRemoveAll = () => {
        onStateChange(tableStateReducer(state, { type: ReducerActionType.RemoveAllFilters }));
        onRowSelectionChange?.({});
    };

    const findColumnDef = (columnName: keyof T) =>
        // @ts-expect-error TS2339: Property 'accessorKey' does not exist
        columns.find((columnDef) => columnDef.id === columnName || columnDef?.accessorKey === columnName);

    const getOptionLabel = (value: string, columnName: keyof T) =>
        findColumnDef(columnName)
            ?.meta?.getMultiSelectOptions?.()
            ?.find((item) => item.value === value)?.label;

    const getBoolLabel = (value: boolean, columnName: keyof T) =>
        findColumnDef(columnName)?.meta?.getBoolOptions?.()?.[value ? 'true' : 'false'];

    const tagEntries = Object.entries(state.filters);

    const valuesAsTags = (tagEntries as ColumnFilterEntries<T>)
        .map(([columnName, columnFilters]) => {
            const columnDef = findColumnDef(columnName);
            return columnFilters?.flatMap((columnFilter) => {
                if (
                    columnDef?.meta?.filtersType === ColumnFilterType.MultiSelect &&
                    columnFilter.filterType === EnumFilterType.In
                ) {
                    return columnFilter.value.map((selectedOption) => (
                        <Chip
                            key={`${String(columnName)}-${selectedOption}`}
                            value={selectedOption}
                            onRemove={() => handleMultiSelectFilterRemove(columnName, columnFilter, selectedOption)}
                        >
                            {getOptionLabel(selectedOption, columnName)}
                        </Chip>
                    ));
                }
                if (
                    columnDef?.meta?.filtersType === ColumnFilterType.Bool &&
                    columnFilter.filterType === BoolFilterType.Equals
                ) {
                    return (
                        <Chip
                            key={`${String(columnName)}-${columnFilter.value}`}
                            value={columnFilter.value}
                            onRemove={() => handleFilterRemove(columnName, columnFilter)}
                        >
                            {getBoolLabel(columnFilter.value, columnName)}
                        </Chip>
                    );
                }
                return (
                    <Chip
                        key={`${String(columnName)}-${String(columnFilter.value)}`}
                        value={String(columnFilter.value)}
                        onRemove={() => handleFilterRemove(columnName, columnFilter)}
                    >
                        {columnFilter.value}
                    </Chip>
                );
            });
        })
        .flat();

    if (valuesAsTags.length === 0) {
        return null;
    }

    return (
        <TagsContainer hasTags={valuesAsTags.length > 0} spacing="50" itemAlignment="center">
            {valuesAsTags}
            {valuesAsTags.length > 1 && (
                <Button variant="ghost" size="small" onClick={handleRemoveAll}>
                    {t(OptimusTextKey.TableClearFilters)}
                </Button>
            )}
        </TagsContainer>
    );
};

export default TableActiveColumnFilters;
